const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/kisapi/responsibilities.ts';
import { compileEndpoint } from './utils';
import { FallartFilter, KlasseFilter, PlanFilter } from '@cistec/common_web/actions/kisapi/search';

export interface SearchResponsibilitiesByVisumParams {
    visum: string; // id of user
    maxdat: string; // mandatory; YYYY-MM-DD HH:mm:ss
    mindat: string; // mandatory; YYYY-MM-DD HH:mm:ss
    insurance?: KlasseFilter; // If specified, Zuständigkeiten cases are filtered by KLASSE
    plan?: PlanFilter; // If specified, Zuständigkeiten cases are filtered by PLAN
    stay?: FallartFilter; // If specified, Zuständigkeiten cases are filtered by FALLART
    withContent?: boolean; // true=load the WorkItem's CONTENT
}

export interface SearchResponsibilitiesByTypeParams {
    typeIcode: string; // Metakat ICODE representing a responsibility type (KISIM Suchfenster Zuständ. type dropdown)
    maxdat: string; // mandatory; YYYY-MM-DD HH:mm:ss
    mindat: string; // mandatory; YYYY-MM-DD HH:mm:ss
    insurance?: KlasseFilter; // If specified, Zuständigkeiten cases are filtered by KLASSE
    plan?: PlanFilter; // If specified, Zuständigkeiten cases are filtered by PLAN
    stay?: FallartFilter; // If specified, Zuständigkeiten cases are filtered by FALLART
    withContent?: boolean; // true=load the WorkItem's CONTENT
}

type SenderReceiver = 'self' | { kat: 'USER' | 'POOL'; key: string };

export interface CreateResponsibilityParams {
    patnr: string; // patient id
    fallnr?: string | null; // case id; optional if Fall-übergreifende Zuständigkeit
    sender?: SenderReceiver; // defines SKAT and SKEY; should usually be set to 'self'
    receiver: SenderReceiver; // defines RKAT and RKEY; specifies who will be responsible for the patient
    tkey: string; // type of Zuständigkeit
    klinik: string; // key of clinic that is involved
    startdat?: string | null; // YYYY-MM-DD HH:mm:ss; null = now
    stopdat?: string | null; // YYYY-MM-DD HH:mm:ss; null = open-ended
}

export interface LoadResponsibilityTypesParams {
    /**
     * The ICODE of the group whose children should be loaded next; null = root level
     */
    pcode: string | null;

    /**
     * Specifies the context in which the list will be used. This affects which access codes will be checked by the
     * endpoint.
     * 'assign': we're creating or modifying a Zuständigkeit;
     * 'filter': we're filtering a list of Zuständigkeiten.
     */
    purpose: 'assign' | 'filter';

    /**
     * Controls whether labels and/or non-labels should be returned.
     * 'require': get the list of labels and nothing else;
     * 'exclude': get the list of non-labels;
     * null: don't care, i.e. return labels as well as non-labels
     */
    labels?: 'require' | 'exclude' | null;

    /**
     * Controls whether Zuständigkeitsgruppen should be returned. Note that this refers to the Gruppe flag in the
     * Zuständigkeitskonfiguration. It does NOT refer to ST (Steuerelement) or similar grouping items. Those items are
     * always included in the response. Valid values are:
     * 'require': get the list of Zuständigkeitsgrouppen and nothing else;
     * 'exclude': get the list of non-Zuständigkeitsgruppen;
     * null: don't care
     */
    zusGroups?: 'require' | 'exclude' | null;

    /**
     * Controls whether configuration entries should be included in the response.
     */
    includeCfgInResponse: boolean;

    /**
     * Controls whether the endpoint is allowed to try to convert Qstructs to arrays.
     * In general, setting this to true (disallow) is preferred, because conversion is only possible with a heuristic.
     */
    disallowArrays?: boolean;
}

/**
 * Loads the list of responsibilities that indicate KISIM users who are/were responsible for a given patient and case
 * number inside a given time window.
 * @param patnr patient id
 * @param fallnr case number
 * @param mindat mandatory; YYYY-MM-DD HH:mm:ss
 * @param maxdat mandatory; YYYY-MM-DD HH:mm:ss
 * @param withContent true=load the WorkItem's CONTENT
 * @param newStructure true=return case information, patient information and workItem in separate subtrees, which is
 *    consistent with apiSearchResponsibilitiesByType
 * @param tkeys Optional list of allowed TKEYs (representing Zuständigkeitstyp)
 */
export function apiLoadResponsibilitiesByPat(
    patnr: string,
    fallnr: string | undefined,
    mindat: string | undefined,
    maxdat: string | undefined,
    withContent: boolean = false,
    newStructure: boolean = false,
    tkeys: string[] | undefined = undefined
) {
    if (tkeys && !tkeys.length) {
        // Without this check, TKEYList would become NULL in QSCRIPT, and the endpoint would ignore TKEYs, which is not
        // what we'd expect.
        throw new Error('List of allowed TKEYs cannot be empty; pass undefined to ignore');
    }

    return {
        endpoint: compileEndpoint(
            ['KisMobile', 'Zustaendigkeit', 'PatientSearch'],
            [
                { key: 'PatNr', value: patnr },
                { key: 'FallNr', value: fallnr },
                { key: 'MINDAT', value: mindat },
                { key: 'MAXDAT', value: maxdat },
                !withContent ? null : { key: 'WithContent', value: '1' },
                !newStructure ? null : { key: 'NewStructure', value: '1' },
                !tkeys ? null : { key: 'TKEYList', value: tkeys.join(',') },
            ]
        ),
        requestConfig: {
            headers: { Accept: 'array=no' },
        },
    };
}

/**
 * @deprecated
 * Loads the list of responsibilities that indicate KISIM users wo are/were responsible for the given list of patients
 * and their cases. Note that this is a special-purpose endpoint that's mainly used by Teamboard. However, Teamboard
 * has its own action for it (endpointKisLoadZustaendigkeit), so this action here will probably only be used by
 * KISAPI tests. Also note that the response returned by this endpoint is not compatible with the other APIs such as
 * apiSearchResponsibilitiesByVisum, etc.
 * @param patnrs List of patients of interest
 * @param fallnrs List of cases; must match the given list of patients. (Cases that do not belong to any of the given
 *    patients will be ignored, unless there is a WorkItem that also has that incorrect combination in its members.)
 * @param tkeys List of TKEYs (types of Zustaendigkeit)
 * @param mindat YYYY-MM-DD HH:mm:ss
 * @param maxdat YYYY-MM-DD HH:mm:ss
 */
export function apiLoadResponsibilitiesByPatFallTkey(
    patnrs: string[],
    fallnrs: string[] | null,
    tkeys: string[],
    mindat: string,
    maxdat: string
) {
    return {
        endpoint: compileEndpoint(
            ['KisMobile', 'Zustaendigkeit', 'PatientSet'],
            [
                { key: 'PATNRlist', value: patnrs.join(',') },
                !fallnrs ? null : { key: 'FALLNRlist', value: fallnrs.join(',') },
                { key: 'TKEYlist', value: tkeys.join(',') },
                { key: 'MINDAT', value: mindat },
                { key: 'MAXDAT', value: maxdat },
            ]
        ),
        requestConfig: {
            headers: { Accept: 'array=no' },
        },
    };
}

/**
 * Loads the list of responsibilities indicating the patients that were assigned to a given KISIM user inside a given
 * time window.
 */
export function apiSearchResponsibilitiesByVisum(params: SearchResponsibilitiesByVisumParams) {
    const { insurance, maxdat, mindat, plan, stay, visum, withContent = false } = params;
    return {
        endpoint: compileEndpoint(
            ['KisMobile', 'Zustaendigkeit', 'Search'],
            [
                { key: 'Visum', value: visum },
                { key: 'MINDAT', value: mindat },
                { key: 'MAXDAT', value: maxdat },
                !withContent ? null : { key: 'WithContent', value: '1' },
                !plan ? null : { key: 'Plan', value: plan },
                !stay ? null : { key: 'Fallart', value: stay },
                !insurance ? null : { key: 'Klasse', value: insurance },
            ]
        ),
        requestConfig: {
            headers: { Accept: 'array=no' },
        },
    };
}

/**
 * Searches responsibilities by type.
 */
export function apiSearchResponsibilitiesByType(params: SearchResponsibilitiesByTypeParams) {
    const { insurance, maxdat, mindat, plan, stay, typeIcode, withContent = false } = params;
    return {
        endpoint: compileEndpoint(
            ['KisMobile', 'Zustaendigkeit', 'Search'],
            [
                { key: 'TypeIcode', value: typeIcode },
                { key: 'MINDAT', value: mindat },
                { key: 'MAXDAT', value: maxdat },
                !withContent ? null : { key: 'WithContent', value: '1' },
                !plan ? null : { key: 'Plan', value: plan },
                !stay ? null : { key: 'Fallart', value: stay },
                !insurance ? null : { key: 'Klasse', value: insurance },
            ]
        ),
        requestConfig: {
            headers: { Accept: 'array=no' },
        },
    };
}

/**
 * Loads the list of available Zuständigkeitstypen. The list can be quite large, that's why the endpoint does not
 * support getting the full list. Instead, levels of the hierarchy need to be loaded one by one as needed.
 */
export function apiLoadResponsibilityTypes({
    pcode,
    purpose,
    labels = null,
    zusGroups = null,
    includeCfgInResponse,
    disallowArrays = true,
}: LoadResponsibilityTypesParams) {
    return {
        endpoint: compileEndpoint(
            ['KisMobile', 'Zustaendigkeit', 'Types'],
            [
                { key: 'PCODE', value: pcode ?? '' },
                { key: 'Purpose', value: purpose },
                labels === null ? null : { key: 'Labels', value: labels },
                zusGroups === null ? null : { key: 'ZusGroups', value: zusGroups },
                !includeCfgInResponse ? null : { key: 'WithCfg', value: '1' },
            ]
        ),
        requestConfig: {
            headers: {
                Accept: disallowArrays ? 'array=no' : 'application/json',
            },
        },
    };
}

/**
 * Creates a new responsibility entry.
 */
export function apiCreateResponsibility(params: CreateResponsibilityParams) {
    const { patnr, fallnr = null, sender = 'self', receiver, tkey, klinik, startdat = null, stopdat = null } = params;

    return {
        endpoint: compileEndpoint(['KisMobile', 'Zustaendigkeit', 'New'], []),
        requestConfig: {
            method: 'POST' as const,
            headers: {
                Accept: 'array=no', // implies application/json
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                PATNR: patnr,
                FALLNR: fallnr || '0',
                SKAT: sender === 'self' ? '' : sender.kat,
                SKEY: sender === 'self' ? '' : sender.key,
                RKAT: receiver === 'self' ? '' : receiver.kat,
                RKEY: receiver === 'self' ? '' : receiver.key,
                TKEY: tkey,
                KLINIK: klinik,
                STARTDAT: startdat ?? '',
                STOPDAT: stopdat ?? '',
            }),
        },
    };
}

/**
 * Updates a responsibility entry or creates a followup. The endpoint will update the existing entry if only the STATUS
 * changes and nothing else. If more than the STATUS changes, it will create a new followup entry. In either case, the
 * updated or newly created record will be returned in the payload.
 * @param workItem Partial WorkItem record
 * @param etag ETAG of the Zuständigkeit (you can't obtain this via WorkItem endpoint)
 * @param disallowArrays true=tell endpoint not to try to convert Qstructs to arrays (recommended)
 */
export function apiUpdateResponsibility(
    workItem: Record<string, unknown>,
    etag: string,
    disallowArrays: boolean = false
) {
    const workId = workItem.WORKID as string;
    if (!workId) throw new Error('WORKID is mandatory');
    if (!etag) throw new Error('ETAG is mandatory');
    return {
        endpoint: compileEndpoint(['KisMobile', 'Zustaendigkeit', workId], []),
        requestConfig: {
            method: 'POST' as const,
            headers: {
                Accept: disallowArrays ? 'array=no' : 'application/json',
                'Content-Type': 'application/json',
                'If-Match': etag,
            },
            body: JSON.stringify(workItem),
        },
    };
}

/**
 * Deletes a responsibility entry.
 * @param workId Id of the WorkItem record
 * @param etag ETAG of the Zuständigkeit (you can't obtain this via WorkItem endpoint)
 * @param disallowArrays true=tell endpoint not to try to convert Qstructs to arrays (recommended)
 */
export function apiDeleteResponsibility(workId: string, etag: string, disallowArrays: boolean = false) {
    if (!workId) throw new Error('WORKID is mandatory');
    if (!etag) throw new Error('ETAG is mandatory');
    return {
        endpoint: compileEndpoint(['KisMobile', 'Zustaendigkeit', workId], []),
        requestConfig: {
            method: 'DELETE' as const,
            headers: {
                Accept: disallowArrays ? 'array=no' : 'application/json',
                'Content-Type': 'application/json',
                'If-Match': etag,
            },
        },
    };
}
